import { useAbility } from '@casl/react';
import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  lighten,
  useTheme,
} from '@mui/material';
import { ACTIONS } from 'src/auth/utils/defineAbilityFor';
import EntityIcon from 'src/components/Icons/EntityIcon';
import { AbilityContext } from 'src/context/canContext';
import { useAdminUrlNavigation } from 'src/hooks/useAdminUrlNavigation';
import useEffectiveApplicationGroupId from 'src/hooks/useEffectiveApplicationGroupId';
import useEffectiveApplicationId from 'src/hooks/useEffectiveApplicationId';
import { useGetAllUsersQuery } from 'src/services/switch-rest-api/user.service';
import WidgetCard from 'switch-fe-shared/components/Cards/WidgetCard';

import { formatNumber } from '../../../../../../switch-shared/src/utils/formatters';
import { isAllSelection } from '../../../../utils/selection';
import { useAllGroupsMetrics } from '../../hooks/useAllGroupsMetrics';

const UsersWidget = () => {
  /* Hooks & Use Effects */

  const theme = useTheme();
  const ability = useAbility(AbilityContext);
  const { navigateToUsers } = useAdminUrlNavigation();

  const appId = useEffectiveApplicationId();
  const appGroupId = useEffectiveApplicationGroupId();

  /* States */
  const isAllGroupsSelected = isAllSelection(appGroupId);

  /* All Groups Metrics Hook */
  const {
    metrics: allGroupsMetrics,
    isLoading: isAllGroupsLoading,
    isFetching: isAllGroupsFetching,
  } = useAllGroupsMetrics();

  /* API Calls */

  const {
    data: users,
    isLoading,
    isFetching,
  } = useGetAllUsersQuery(
    {
      applicationGroupId: !isAllSelection(appGroupId) ? appGroupId : undefined,
      applicationId: !isAllSelection(appId) ? appId : undefined,
    },
    {
      skip: isAllGroupsSelected,
      pollingInterval: 30_000,
    }
  );

  /* Functions & Functional Components */

  const getTooltipDescription = () => {
    if (isAllGroupsSelected) {
      return 'The total number of assigned users across all groups';
    }
    if (appId != null && !isAllSelection(appId)) {
      return 'The total number of users assigned to the selected application';
    }
    return 'The total number of users in the selected group';
  };

  return (
    <WidgetCard
      title='Users'
      subheader='Total count'
      tooltipDescription={getTooltipDescription()}
      disabled={!ability.can(ACTIONS.READ, 'Users')}
      avatar={
        <Box
          sx={{
            padding: `${theme.tokens.spacing.sm}px`,
            height: `${theme.tokens.spacing.sm * 5}px`,
            width: `${theme.tokens.spacing.sm * 5}px`,
            mx: `-${theme.tokens.spacing.sm}px`,
            borderRadius: theme.tokens.borderRadius.full,
            background: theme.tokens.colors.card,
            boxShadow: theme.tokens.shadows.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.primary.main,
          }}
        >
          <EntityIcon type='users' />
        </Box>
      }
      onClickFn={
        !isAllGroupsSelected ? () => navigateToUsers(appGroupId) : undefined
      }
      cardContentProps={{
        sx: {
          pt: `0 !important`,
          pb: `16px !important`,
          color: lighten(theme.palette.primary.main, 0.1),
        },
      }}
    >
      <Grid
        container
        alignContent={'center'}
        justifyContent={'center'}
        height={'100%'}
      >
        <Grid item>
          {isAllGroupsSelected ? (
            <>
              {isAllGroupsLoading || isAllGroupsFetching ? (
                <CircularProgress />
              ) : (
                <Typography variant='h4' fontSize={'25px'} fontWeight={'bold'}>
                  {formatNumber(allGroupsMetrics?.assignedUsers ?? 0, 0)}
                </Typography>
              )}
            </>
          ) : (
            <>
              {isLoading || isFetching ? (
                <CircularProgress />
              ) : (
                <Typography variant='h4' fontSize={'25px'} fontWeight={'bold'}>
                  {formatNumber(users?.totalRecords ?? 0, 0)}
                </Typography>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </WidgetCard>
  );
};

export default UsersWidget;
